/**
 * #1310 PR-N — "Suggest stitches" button on needs-review flow rows.
 *
 * Visible only when the row's classification is 'needs-review'.
 * Click → `POST /:id/stitches/propose { dryRun: true }`. The dryRun
 * flag (added in #1289 / #1280 PR 9) routes AI proposals into the
 * `stitches.url.proposals` pending bucket — the classifier ignores
 * those until the user confirms via the review modal.
 *
 * Shows a small spinner while the request is in flight; resets to a
 * "Pending review (N)" badge on success so the user sees the
 * proposals are queued.
 *
 * #1310 PR-P — the pending badge is now a button that opens the
 * `StitchProposalReview` modal. Confirming a proposal there fires
 * the `onProposalConfirmed` callback so the parent (catalog) can
 * refetch counts and let the classification badge re-render.
 */
import { useState } from 'react';
import { StitchProposalReview } from './StitchProposalReview';
import { apiFetch } from '../../services/api';

interface SuggestStitchesButtonProps {
  projectId: string;
  /** Optional: when present, only this call site gets proposals. */
  callSiteId?: string;
  /**
   * #1310 PR-P — fires whenever a proposal is confirmed or rejected.
   * Parent can use this to invalidate `useCatalog` so the row's
   * classification badge re-fetches.
   */
  onProposalConfirmed?: () => void;
}

type ButtonState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'pending'; count: number }
  | { status: 'error'; message: string };

export function SuggestStitchesButton({
  projectId,
  callSiteId,
  onProposalConfirmed,
}: SuggestStitchesButtonProps) {
  const [state, setState] = useState<ButtonState>({ status: 'idle' });
  const [reviewing, setReviewing] = useState(false);

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    setState({ status: 'loading' });
    try {
      const r = await apiFetch(`/projects/${projectId}/stitches/propose`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dryRun: true,
          ...(callSiteId ? { callSiteId } : {}),
        }),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const body = (await r.json()) as { proposals?: unknown[]; count?: number };
      const count =
        typeof body.count === 'number'
          ? body.count
          : Array.isArray(body.proposals)
            ? body.proposals.length
            : 0;
      setState({ status: 'pending', count });
    } catch (err) {
      setState({ status: 'error', message: (err as Error).message });
    }
  }

  if (state.status === 'pending') {
    return (
      <>
        <button
          onClick={e => {
            e.stopPropagation();
            setReviewing(true);
          }}
          className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
          title={`${state.count} proposal(s) pending your review — click to review`}
        >
          pending review ({state.count})
        </button>
        {reviewing && (
          <StitchProposalReview
            projectId={projectId}
            onClose={() => setReviewing(false)}
            onChange={() => {
              // Optimistic decrement so the badge count reflects the
              // accept/reject; the parent's onProposalConfirmed fires
              // a refetch that will produce the authoritative number.
              setState(s =>
                s.status === 'pending'
                  ? {
                      status: 'pending',
                      count: Math.max(0, s.count - 1),
                    }
                  : s,
              );
              onProposalConfirmed?.();
            }}
          />
        )}
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <span
        className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400"
        title={state.message}
      >
        suggest failed
      </span>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={state.status === 'loading'}
      className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
      title="Ask the analyzer to suggest stitches for this flow's needs-review API calls"
    >
      {state.status === 'loading' ? 'suggesting…' : 'suggest stitches'}
    </button>
  );
}
